import { getSocketUrl } from "../websocketConfig";

const OPEN = 1;
const MAX_BACKOFF_MS = 15000;
const PRESENCE_INTERVAL_MS = 60_000;

let socket = null;
let identity = null;
let intentionalClose = false;
let reconnectTimer = null;
let presenceTimer = null;
let backoffMs = 1000;
const handlers = new Set();
const pending = [];

function resolveWsEnabled() {
  return (
    process.env.REACT_APP_ENABLE_WS === "true" ||
    process.env.NODE_ENV === "production" ||
    !!(
      typeof import.meta !== "undefined" &&
      import.meta.env &&
      (import.meta.env.VITE_ENABLE_WS === "true" ||
        import.meta.env.VITE_WS_URL ||
        import.meta.env.REACT_APP_WS_URL)
    )
  );
}

function flushPending() {
  if (!socket || socket.readyState !== OPEN) return;
  while (pending.length) {
    const payload = pending.shift();
    try {
      socket.send(JSON.stringify(payload));
    } catch (_e) {
      pending.unshift(payload);
      break;
    }
  }
}

function sendIdentify() {
  if (!identity || !socket || socket.readyState !== OPEN) return;
  socket.send(
    JSON.stringify({
      request: "identify",
      body: {
        identityId: identity.identityId || null,
        userRole: identity.userRole || null,
        userSub: identity.userSub || null,
        employeeId: identity.employeeId || null,
        ipv4: identity.ipv4 || null,
        localIp: identity.localIp || null,
        userAgent: identity.userAgent || null,
        deviceType: identity.deviceType || null,
        sessionId: identity.sessionId || null,
      },
    })
  );
}

function sendPresencePing() {
  if (!socket || socket.readyState !== OPEN) return;
  try {
    socket.send(
      JSON.stringify({
        request: "presence_ping",
        body: {
          localIp: identity?.localIp || null,
        },
      })
    );
  } catch (_e) {
    // ignore
  }
}

function startPresencePing() {
  stopPresencePing();
  presenceTimer = setInterval(sendPresencePing, PRESENCE_INTERVAL_MS);
}

function stopPresencePing() {
  if (presenceTimer) {
    clearInterval(presenceTimer);
    presenceTimer = null;
  }
}

function scheduleReconnect() {
  if (intentionalClose || !resolveWsEnabled()) return;
  if (reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
    backoffMs = Math.min(backoffMs * 1.5, MAX_BACKOFF_MS);
  }, backoffMs);
}

function connect() {
  if (!resolveWsEnabled()) return;
  if (
    socket &&
    (socket.readyState === OPEN || socket.readyState === 0 /* CONNECTING */)
  ) {
    return;
  }

  intentionalClose = false;
  try {
    socket = new WebSocket(getSocketUrl("/dashboard/sockets"));
  } catch (err) {
    console.warn("WebSocket unavailable:", err);
    scheduleReconnect();
    return;
  }

  socket.onopen = () => {
    backoffMs = 1000;
    sendIdentify();
    flushPending();
    startPresencePing();
  };

  socket.onmessage = (event) => {
    let parsed;
    try {
      parsed = JSON.parse(event.data);
    } catch (_e) {
      return;
    }
    handlers.forEach((handler) => {
      try {
        handler(parsed);
      } catch (err) {
        console.error("dashboardSocket handler error", err);
      }
    });
  };

  socket.onerror = () => {
    // onclose will reconnect
  };

  socket.onclose = () => {
    stopPresencePing();
    socket = null;
    scheduleReconnect();
  };
}

/**
 * Set user identity for identify handshake (call after auth resolves).
 */
export function setSocketIdentity(next) {
  identity = next || null;
  if (socket && socket.readyState === OPEN) {
    sendIdentify();
  } else {
    connect();
  }
}

/**
 * Subscribe to inbound messages. Returns unsubscribe.
 */
export function subscribe(handler) {
  if (typeof handler !== "function") return () => {};
  handlers.add(handler);
  connect();
  return () => {
    handlers.delete(handler);
  };
}

/**
 * Publish an outbound message. Queues while reconnecting.
 */
export function publish(message) {
  if (!message || typeof message !== "object") return;
  connect();
  if (socket && socket.readyState === OPEN) {
    try {
      socket.send(JSON.stringify(message));
      return;
    } catch (_e) {
      // fall through to queue
    }
  }
  pending.push(message);
}

export function closeDashboardSocket() {
  intentionalClose = true;
  stopPresencePing();
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (socket) {
    try {
      socket.close();
    } catch (_e) {
      // ignore
    }
    socket = null;
  }
}

/** @deprecated Prefer publish/subscribe — kept for gradual migration */
export function getDashboardSocket() {
  connect();
  return {
    get readyState() {
      return socket ? socket.readyState : 3;
    },
    OPEN,
    send(data) {
      if (typeof data === "string") {
        try {
          publish(JSON.parse(data));
        } catch (_e) {
          publish({ request: "raw", body: data });
        }
      } else {
        publish(data);
      }
    },
    close: closeDashboardSocket,
  };
}
